<template>
  <ModalStep
    id="group-structure-content"
    data-test-id="admin-reminder-popup"
    :header="{
      title: `${t('admin.reminders.reminderStructureModal.new.title')}`,
    }"
  >
    <template #bodyContent>
      <LoadingContent v-if="reminderIdRef && isReminderPendingRef" />
      <template v-else>
        <div>
          <CustomInput
            type="text"
            id="reminder-name"
            data-test-id="reminder-name-input"
            :label="`${t('reminder.newReminderModal.generalInformation.input.name.label')}`"
            :placeholder="t('reminder.newReminderModal.generalInformation.input.name.placeholder')"
            @emit:change="reminderNameHandler"
            :value="reminderNameRef"
          />
          <CustomSelect
            id="reminder-group"
            data-test-id="reminder-program-select"
            :options="programsOptionsRef"
            :label="`${t('reminder.newReminderModal.generalInformation.input.program.label')} *`"
            @emit:change="reminderProgramHandler"
            :value="selectedProgramIdsRef"
            :isMultiple="true"
          />
          <div class="reminder-cycle-dates">
            <CustomInput
              type="date"
              id="reminder-start-date"
              data-test-id="reminder-start-date-input"
              :label="`${t('reminder.newReminderModal.generalInformation.input.start-date.label')} *`"
              :placeholder="t('reminder.newReminderModal.generalInformation.input.start-date.placeholder')"
              @emit:change="cycleStartDateHandler"
              :value="moment(cycleStartDateRef).format('YYYY-MM-DD')"
              :minDate="dayjsClient(selectedProgramRef?.latestPeriod?.startAt).format('YYYY-MM-DD')"
              :maxDate="dayjsClient(selectedProgramRef?.latestPeriod?.endAt).format('YYYY-MM-DD')"
            />
            <CustomInput
              type="date"
              id="reminder-end-date"
              data-test-id="reminder-end-date-input"
              :label="`${t('reminder.newReminderModal.generalInformation.input.end-date.label')} *`"
              :placeholder="t('reminder.newReminderModal.generalInformation.input.end-date.placeholder')"
              @emit:change="cycleEndDateHandler"
              :value="moment(cycleEndDateRef).format('YYYY-MM-DD')"
              :minDate="dayjsClient(cycleStartDateRef).format('YYYY-MM-DD')"
              :maxDate="dayjsClient(selectedProgramRef?.latestPeriod?.endAt).format('YYYY-MM-DD')"
            />
          </div>
          <div v-if="cycleStartDateRef && cycleEndDateRef" class="reminder-reminder-dates">
            <div class="reminder-reminder-dates__add">
              <div class="reminder-reminder-dates__add-inputs">
                <CustomInput
                  type="date"
                  id="reminder-date"
                  data-test-id="reminder-date-input"
                  :label="`${t('reminder.newReminderModal.generalInformation.input.reminder-date.label')} *`"
                  :placeholder="t('reminder.newReminderModal.generalInformation.input.reminder-date.placeholder')"
                  @emit:change="reminderDateHandler"
                  :value="reminderDateRef"
                  :minDate="
                    dayjsClient(new Date(cycleStartDateRef) > today ? cycleStartDateRef : today).format('YYYY-MM-DD')
                  "
                  :maxDate="dayjsClient(cycleEndDateRef).format('YYYY-MM-DD')"
                />

                <CustomSelect
                  id="reminder-type"
                  data-test-id="reminder-type-select"
                  :options="reminderCats"
                  :label="`Type de relance *`"
                  @emit:change="reminderTypeHandler"
                  :value="reminderTypesRef"
                  :isMultiple="true"
                />
              </div>

              <CustomButton
                usage="button"
                type="submit"
                data-test-id="add-reminder-date-button"
                color="primary"
                :text="t('reminder.newReminderModal.generalInformation.buton.reminder-date')"
                @emit:click="addReminderDate"
              />
            </div>

            <div class="reminder-reminder-dates__dates">
              <div
                :class="`border border-gray-300 rounded p-2 flex items-center justify-between ${new Date(reminder.reminderDate) < today ? 'bg-gray-100' : ''}`"
                v-for="reminder in remindersDatesRef"
                :key="`${reminder.reminderDate}-${reminder.reminderTypes.sort().join('-')}`"
                data-test-id="reminder-date-item"
              >
                <div>
                  <p>{{ moment(new Date(reminder.reminderDate)).format('DD MMMM YYYY') }}</p>
                  <div class="flex gap-1 mt-1">
                    <Badge
                      v-for="type in reminder.reminderTypes"
                      :key="type"
                      variant="info"
                      data-test-id="reminder-type-badge"
                      custom-class="text-xs px-2 py-0.5 flex items-center gap-1"
                    >
                      {{ reminderCats.find((cat) => cat.value === type)?.label || type }}
                      <div
                        v-if="new Date(reminder.reminderDate) > today"
                        @click.stop="removeSingleReminderType(reminder, type)"
                        class="ml-1 cursor-pointer hover:bg-gray-200 rounded-full p-0.5 flex items-center justify-center w-4 h-4"
                        title="Supprimer ce type de rappel"
                      >
                        <CrossIcon data-test-id="remove-reminder-type" color="black" />
                      </div>
                    </Badge>
                  </div>
                </div>
                <div
                  v-if="new Date(reminder.reminderDate) > today"
                  @click="removeReminder(reminder)"
                  class="reminder-reminder-dates__dates__date__cross"
                >
                  <CrossIcon color="danger" data-test-id="remove-reminder" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="submit"
          data-test-id="reminder-structure-modal-close-button"
          color="white"
          :text="t('ui.button.cancel')"
          @emit:click="onClose"
        />
        <CustomButton
          v-if="reminderRef.id"
          usage="button"
          type="button"
          data-test-id="reminder-structure-modal-delete-button"
          color="danger"
          :text="t('ui.button.delete')"
          @emit:click="onDelete"
        />
        <CustomButton
          :is-loading="isSubmittingRef"
          usage="button"
          type="submit"
          data-test-id="reminder-structure-modal-submit-button"
          color="primary"
          :text="reminderRef.id ? t('admin.updateReminderModal.submit') : t('admin.newReminderModal.submit')"
          @emit:click="onSubmit"
        >
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import moment from 'moment'
import { computed, watch } from 'vue'
import { ref } from 'vue'

import { createReminderMutation, deleteCycleMutation, updateReminderMutation } from '@/features/admin/reminders/api'
import useReminder from '@/features/admin/reminders/hooks/use-reminder.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import Badge from '@/components/Badge.vue'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CrossIcon from '@/features/ui/components/icons/cross-icon.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import CustomSelect from '@/features/ui/components/select/custom-select.vue'
import { selectGetReminderStructureModal } from '@/features/ui/store/modal/modal.selectors'
import type { ConfirmActionModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { invalidateAllReminders, invalidateReminder } from '@/utils/lib/vue-query'

const { t } = useI18n()
const toast = useToast()
const tempSelectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const reminderStructureModalRef = selectGetReminderStructureModal(modalsStore)
const programs = usePrograms()
const programsOptionsRef = computed(() =>
  programs.programsRef.value.map((program) => ({ value: program.id, label: program.name })),
)
const reminderCats = [
  { value: 'SESSION', label: 'Session' },
  { value: 'MATCHING_MENTEE', label: 'Matching Mentorés' },
  { value: 'MATCHING_MENTOR', label: 'Matching Mentors' },
  { value: 'CONVERSATION_INACTIVITY', label: 'Premier message' },
  { value: 'SESSION_FEEDBACK', label: 'Feedback de session' },
]
const reminderIdRef = computed(() => reminderStructureModalRef.value.attachedData.reminderId ?? null)
const { reminderRef, isPendingRef: isReminderPendingRef } = useReminder(reminderIdRef)

const today = new Date()

// reminder name
const reminderNameRef = ref<string | null>(null)
function reminderNameHandler(value: string) {
  console.log(value)
  reminderNameRef.value = value
}

// program
const selectedProgramIdsRef = ref<string[]>(tempSelectedProgramIdRef.value ? [tempSelectedProgramIdRef.value] : [])
function reminderProgramHandler(values: string[]) {
  console.log(values)
  selectedProgramIdsRef.value = values
}
const { programRef: selectedProgramRef } = useProgram(computed(() => selectedProgramIdsRef.value[0] ?? null))

// startDate
const cycleStartDateRef = ref<string | null>(null)
function cycleStartDateHandler(value: string) {
  console.log(value)
  cycleStartDateRef.value = value

  const startDate = new Date(value)
  const now = new Date()

  remindersDatesRef.value = remindersDatesRef.value.filter((reminder) => {
    const reminderDate = new Date(reminder.reminderDate)
    return !(reminderDate < startDate && reminderDate > now)
  })
}

// endDate
const cycleEndDateRef = ref<string | null>(null)
function cycleEndDateHandler(value: string) {
  console.log(value)
  cycleEndDateRef.value = value
  for (let i = 0; i < remindersDatesRef.value.length; i++) {
    if (remindersDatesRef.value[i].reminderDate > cycleEndDateRef.value) {
      remindersDatesRef.value.splice(i, 1)
    }
  }
}

//reminder date
const remindersDatesRef = ref<any[]>([])
const reminderDateRef = ref<string | null>(null)
const reminderTypesRef = ref<string[]>([reminderCats[0].value])

function reminderDateHandler(value: string) {
  console.log(value)
  reminderDateRef.value = value
}

function reminderTypeHandler(values: string[]) {
  console.log(values)
  reminderTypesRef.value = values
}

function addReminderDate() {
  if (reminderDateRef.value && reminderTypesRef.value.length > 0) {
    remindersDatesRef.value.push({
      reminderDate: reminderDateRef.value,
      reminderTypes: [...reminderTypesRef.value],
    })
    reminderDateRef.value = null
    reminderTypesRef.value = [reminderCats[0].value]

    // Tri des dates après l'ajout
    remindersDatesRef.value = remindersDatesRef.value.sort(
      (a, b) => new Date(a.reminderDate).getTime() - new Date(b.reminderDate).getTime(),
    )
    console.log(remindersDatesRef.value)
  }
}

function removeReminder(reminder: any) {
  remindersDatesRef.value = remindersDatesRef.value.filter(
    (r) =>
      r.reminderDate !== reminder.reminderDate ||
      JSON.stringify(r.reminderTypes.sort()) !== JSON.stringify(reminder.reminderTypes.sort()),
  )

  // Tri des dates après la suppression
  remindersDatesRef.value = remindersDatesRef.value.sort(
    (a, b) => new Date(a.reminderDate).getTime() - new Date(b.reminderDate).getTime(),
  )
  console.log(remindersDatesRef.value)
}

function removeSingleReminderType(reminder: any, typeToRemove: string) {
  const reminderIndex = remindersDatesRef.value.findIndex(
    (r) =>
      r.reminderDate === reminder.reminderDate &&
      JSON.stringify(r.reminderTypes.sort()) === JSON.stringify(reminder.reminderTypes.sort()),
  )

  if (reminderIndex !== -1) {
    const updatedReminder = { ...remindersDatesRef.value[reminderIndex] }
    updatedReminder.reminderTypes = updatedReminder.reminderTypes.filter((type: string) => type !== typeToRemove)

    // Si plus aucun type, supprimer complètement le rappel
    if (updatedReminder.reminderTypes.length === 0) {
      remindersDatesRef.value.splice(reminderIndex, 1)
    } else {
      remindersDatesRef.value[reminderIndex] = updatedReminder
    }

    // Tri des dates après la modification
    remindersDatesRef.value = remindersDatesRef.value.sort(
      (a, b) => new Date(a.reminderDate).getTime() - new Date(b.reminderDate).getTime(),
    )
    console.log(remindersDatesRef.value)
  }
}

watch(
  reminderRef,
  () => {
    if (reminderRef.value) {
      reminderNameRef.value = reminderRef.value.name ?? null
      selectedProgramIdsRef.value = reminderRef.value.programIds ?? []
      cycleStartDateRef.value = reminderRef.value.startDate ?? null
      cycleEndDateRef.value = reminderRef.value.endDate ?? null
      // Pour la compatibilité, on groupe les rappels par date et on rassemble les types
      const remindersByDate = new Map()
      reminderRef.value.reminders?.forEach((reminder) => {
        const date = reminder.reminderDate
        const type = reminder.reminderType || 'SESSION'

        if (remindersByDate.has(date)) {
          remindersByDate.get(date).reminderTypes.push(type)
        } else {
          remindersByDate.set(date, {
            reminderDate: date,
            reminderTypes: [type],
          })
        }
      })

      remindersDatesRef.value = Array.from(remindersByDate.values())
      remindersDatesRef.value = remindersDatesRef.value.sort(
        (a, b) => new Date(a.reminderDate).getTime() - new Date(b.reminderDate).getTime(),
      )
    }
  },
  { immediate: true },
)

const isSubmittingRef = ref(false)

const onSubmit = async () => {
  let reminderId = reminderIdRef.value

  // Transformer les données pour l'API : déplier les types multiples
  const apiReminders = remindersDatesRef.value.flatMap((reminder) =>
    reminder.reminderTypes.map((type: string) => ({
      reminderDate: reminder.reminderDate,
      reminderType: type,
    })),
  )

  try {
    if (!reminderId) {
      isSubmittingRef.value = true
      const createReminderResponseData = await createReminderMutation({
        name: reminderNameRef.value || '',
        programIds: selectedProgramIdsRef.value,
        startDate: cycleStartDateRef.value || '',
        endDate: cycleEndDateRef.value || '',
        reminders: apiReminders,
      })
      reminderId = createReminderResponseData.id
      toast?.success(t('admin.reminders.reminderStructureModal.new.success'))
    } else {
      await updateReminderMutation({
        id: reminderId,
        name: reminderNameRef.value || '',
        programIds: selectedProgramIdsRef.value,
        startDate: cycleStartDateRef.value || '',
        endDate: cycleEndDateRef.value || '',
        reminders: apiReminders,
      })
      toast?.success(t('admin.reminders.reminderStructureModal.update.success'))
    }
    await invalidateReminder()
    await invalidateAllReminders()

    modalsStore.updateModalAttachedData('admin-reminder-structure', { reminderId: null })
    modalsStore.toggleModal('admin-reminder-structure', false)
  } catch (e) {
    toast?.error(t('error.common'))
  } finally {
    isSubmittingRef.value = false
  }
}

async function onDelete() {
  modalsStore.toggleModal('confirm-action', true)
  modalsStore.updateModalAttachedData<ConfirmActionModalType>('confirm-action', {
    title: t('admin.reminders.delete.confirmation.title'),
    content: t('admin.reminders.delete.confirmation.content'),
    type: 'danger',
    cancelFn() {
      modalsStore.toggleModal('confirm-action', false)
    },
    async fn() {
      if (!reminderIdRef.value) {
        toast?.error(t('error.common'))
        return
      }
      try {
        await deleteCycleMutation(reminderIdRef.value)
        toast?.success(t('admin.reminders.reminderStructureModal.delete.success'))
        await invalidateAllReminders()
        modalsStore.updateModalAttachedData('admin-reminder-structure', { reminderId: null })
        modalsStore.toggleModal('admin-reminder-structure', false)
      } catch (e) {
        toast?.error(t('error.common'))
      }
    },
  })
}

const onClose = () => {
  if (reminderIdRef.value) {
    modalsStore.updateModalAttachedData('admin-reminder-structure', { reminderId: null })
    modalsStore.toggleModal('admin-reminder-structure', false)
  } else {
    modalsStore.toggleModal('admin-reminder-structure', false)
  }
}
</script>

<style lang="scss">
@import './reminder-structure-modal-content.scss';
</style>
